import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { CellCommentsThreadPopover } from './CellCommentsThreadPopover';

export type CellCommentInteractionMode = 'hover' | 'pinned';

export type CellCommentThreadTarget =
  | { kind: 'project'; projectId: string; projectName: string; columnId: string }
  | { kind: 'reader'; readerPersonId: string; readerName: string; columnId: string }
  | { kind: 'session'; sessionId: string; title: string };

export interface ActiveCellCommentThread {
  target: CellCommentThreadTarget;
  triggerRef: RefObject<HTMLElement | null>;
  mode: CellCommentInteractionMode;
}

export type OpenCellCommentThreadOpts = {
  target: CellCommentThreadTarget;
  triggerRef: RefObject<HTMLElement | null>;
  mode?: CellCommentInteractionMode;
};

const HOVER_DISMISS_MS = 160;

function threadKeysEqual(a: CellCommentThreadTarget, b: CellCommentThreadTarget): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'project' && b.kind === 'project') {
    return a.projectId === b.projectId && a.columnId === b.columnId;
  }
  if (a.kind === 'reader' && b.kind === 'reader') {
    return a.readerPersonId === b.readerPersonId && a.columnId === b.columnId;
  }
  if (a.kind === 'session' && b.kind === 'session') {
    return a.sessionId === b.sessionId;
  }
  return false;
}

interface CellCommentsTableContextValue {
  active: ActiveCellCommentThread | null;
  open: (opts: OpenCellCommentThreadOpts) => void;
  close: () => void;
  notifyThreadUnmount: (activeThread: ActiveCellCommentThread) => void;
  isActiveThread: (target: CellCommentThreadTarget) => boolean;
  isPinnedThread: (target: CellCommentThreadTarget) => boolean;
  cancelHoverDismiss: () => void;
  scheduleHoverDismiss: () => void;
}

const CellCommentsTableContext = createContext<CellCommentsTableContextValue | null>(null);

export function CellCommentsTableProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveCellCommentThread | null>(null);
  const [visualOpen, setVisualOpen] = useState(true);
  const activeRef = useRef<ActiveCellCommentThread | null>(null);
  activeRef.current = active;
  const hoverDismissTimerRef = useRef<number | null>(null);

  const cancelHoverDismiss = useCallback(() => {
    if (hoverDismissTimerRef.current != null) {
      window.clearTimeout(hoverDismissTimerRef.current);
      hoverDismissTimerRef.current = null;
    }
  }, []);

  const scheduleHoverDismiss = useCallback(() => {
    cancelHoverDismiss();
    hoverDismissTimerRef.current = window.setTimeout(() => {
      hoverDismissTimerRef.current = null;
      const a = activeRef.current;
      if (!a || a.mode !== 'hover') return;
      const threadRoot = document.querySelector('[data-cell-comments-thread-root="true"]');
      if (threadRoot instanceof HTMLElement && threadRoot.contains(document.activeElement)) {
        return;
      }
      setVisualOpen(false);
    }, HOVER_DISMISS_MS);
  }, [cancelHoverDismiss]);

  useEffect(() => () => cancelHoverDismiss(), [cancelHoverDismiss]);

  const clearAfterOverlayClose = useCallback(() => {
    cancelHoverDismiss();
    setActive(null);
    setVisualOpen(true);
  }, [cancelHoverDismiss]);

  const open = useCallback(
    (opts: OpenCellCommentThreadOpts) => {
      cancelHoverDismiss();
      setActive({ target: opts.target, triggerRef: opts.triggerRef, mode: opts.mode ?? 'pinned' });
      setVisualOpen(true);
    },
    [cancelHoverDismiss]
  );

  const close = useCallback(() => {
    clearAfterOverlayClose();
  }, [clearAfterOverlayClose]);

  const notifyThreadUnmount = useCallback(
    (thread: ActiveCellCommentThread) => {
      cancelHoverDismiss();
      setActive((a) => (a && threadKeysEqual(a.target, thread.target) ? null : a));
    },
    [cancelHoverDismiss]
  );

  useEffect(() => {
    if (!active) {
      setVisualOpen(true);
    }
  }, [active]);

  const value = useMemo<CellCommentsTableContextValue>(
    () => ({
      active,
      open,
      close,
      notifyThreadUnmount,
      isActiveThread: (target) => Boolean(active && threadKeysEqual(active.target, target)),
      isPinnedThread: (target) =>
        Boolean(active && threadKeysEqual(active.target, target) && active.mode === 'pinned'),
      cancelHoverDismiss,
      scheduleHoverDismiss,
    }),
    [active, open, close, notifyThreadUnmount, cancelHoverDismiss, scheduleHoverDismiss]
  );

  return (
    <CellCommentsTableContext.Provider value={value}>
      {children}
      {active ? (
        <CellCommentsThreadPopover
          key={
            active.target.kind === 'project'
              ? `p:${active.target.projectId}:${active.target.columnId}`
              : active.target.kind === 'reader'
                ? `r:${active.target.readerPersonId}:${active.target.columnId}`
              : `s:${active.target.sessionId}`
          }
          threadTarget={active.target}
          triggerRef={active.triggerRef}
          interactionMode={active.mode}
          isOpen={visualOpen}
          onClose={clearAfterOverlayClose}
          cancelHoverDismiss={cancelHoverDismiss}
          scheduleHoverDismiss={scheduleHoverDismiss}
          onRequestPin={() => {
            const a = activeRef.current;
            if (!a) return;
            open({
              target: a.target,
              triggerRef: a.triggerRef,
              mode: 'pinned',
            });
          }}
        />
      ) : null}
    </CellCommentsTableContext.Provider>
  );
}

export function useCellCommentsTable(): CellCommentsTableContextValue {
  const ctx = useContext(CellCommentsTableContext);
  if (!ctx) {
    throw new Error('useCellCommentsTable должен использоваться внутри CellCommentsTableProvider');
  }
  return ctx;
}

export function useCellCommentsTableOptional(): CellCommentsTableContextValue | null {
  return useContext(CellCommentsTableContext);
}
